/**
 * Execution History Detail — drill-down view for a single automation
 * execution.
 *
 * NEW-MAJOR-2 (round 7) — fixes the missing per-row drill-down on
 * `/automate/history`. The History list now navigates here when a
 * row is clicked. The page renders:
 *
 *   - Header card: workflow name, status badge, trigger, started,
 *     duration, real step counts (matching M1's real aggregation).
 *   - Workflow-level error banner when present.
 *   - Step-by-step trace card: per-step kind, label, status,
 *     duration, error, captured output (collapsed JSON viewer).
 *
 * Data is fetched via `useHistoryExecution(executionId)` against the
 * sibling `GET /automate-executions/:executionId` endpoint added in
 * the same change. The hook polls until the row reaches a terminal
 * state so users who land here mid-run still see the row finalize.
 */

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useState } from 'react';
import {
  ArrowLeft,
  AlertCircle,
  CheckCircle,
  XCircle,
  Loader2,
  Ban,
  Clock,
  Timer,
  Activity,
  ChevronDown,
  ChevronRight,
  Workflow as WorkflowIcon,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import {
  useHistoryExecution,
  type HistoryExecutionDetail,
  type HistoryExecutionStep,
} from '@/lib/use-automate';

export const Route = createFileRoute('/automate/history/$executionId')({
  component: HistoryExecutionDetailPage,
});

// ── Helpers ─────────────────────────────────────────────────────────────

function formatDuration(ms: number | null): string {
  if (ms === null || ms === undefined) return '—';
  if (ms < 1000) return `${ms}ms`;
  const secs = Math.round(ms / 1000);
  if (secs < 60) return `${secs}s`;
  const mins = Math.floor(secs / 60);
  const remainSecs = secs % 60;
  if (mins < 60) return remainSecs > 0 ? `${mins}m ${remainSecs}s` : `${mins}m`;
  const hrs = Math.floor(mins / 60);
  const remainMins = mins % 60;
  return remainMins > 0 ? `${hrs}h ${remainMins}m` : `${hrs}h`;
}

function formatTimestamp(iso: string | null): string {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
}

// Map the broader server-side status vocabulary onto the badge colors.
// Server can emit: pending | queued | running | completed | success |
// succeeded | failed | failure | suspended | skipped | cancelled, plus
// workflow-approval step statuses approved | rejected | denied. Treat terminal
// success/approval as emerald, failure/rejection as red, running/queued as
// blue, suspended as amber, skipped/cancelled as zinc.
function StatusBadge({
  status,
  className,
}: {
  status: string;
  className?: string;
}) {
  const s = status.toLowerCase();
  const isSuccess =
    s === 'success' ||
    s === 'succeeded' ||
    s === 'completed' ||
    s === 'approved';
  const isFailure =
    s === 'failed' || s === 'failure' || s === 'rejected' || s === 'denied';
  const isRunning = s === 'running' || s === 'pending' || s === 'queued';
  const isSuspended = s === 'suspended';
  const isSkipped = s === 'skipped' || s === 'cancelled';
  const cfg = isSuccess
    ? {
        label:
          s === 'approved'
            ? 'Approved'
            : s === 'success'
            ? 'Success'
            : s === 'succeeded'
            ? 'Succeeded'
            : 'Completed',
        Icon: CheckCircle,
        color:
          'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20',
      }
    : isFailure
    ? {
        label: s === 'rejected' || s === 'denied' ? 'Rejected' : 'Failed',
        Icon: XCircle,
        color:
          'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30',
      }
    : isRunning
    ? {
        label:
          s === 'pending' ? 'Pending' : s === 'queued' ? 'Queued' : 'Running',
        Icon: Loader2,
        color:
          'bg-blue-500/10 text-blue-700 dark:text-blue-300 border-blue-500/30',
      }
    : isSuspended
    ? {
        label: 'Suspended',
        Icon: Clock,
        color:
          'bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-500/30',
      }
    : isSkipped
    ? {
        label: s.charAt(0).toUpperCase() + s.slice(1),
        Icon: Ban,
        color:
          'bg-zinc-500/10 text-zinc-700 dark:text-zinc-300 border-zinc-500/30',
      }
    : {
        label: status,
        Icon: Activity,
        color:
          'bg-zinc-500/10 text-zinc-700 dark:text-zinc-300 border-zinc-500/30',
      };
  const Icon = cfg.Icon;
  return (
    <Badge
      variant="outline"
      className={cn('text-[10px] gap-1', cfg.color, className)}
    >
      <Icon
        className={cn('h-3 w-3', isRunning && 'animate-spin')}
        aria-hidden
      />
      {cfg.label}
    </Badge>
  );
}

// ── Step card with collapsible output ──────────────────────────────────

// #1101 — extract the supervisor verdict from `step.output._supervisor`
// so the History detail can show it as a distinct UI element instead of
// burying it inside the raw JSON dump. The runner sets this field at
// `_supervisor: { result, feedback, matchedCriteria }` (see
// `automate-runner.service.ts` SupervisorVerdict + the step-finalize
// path where `enriched.output._supervisor = verdict`).
type SupervisorVerdictResult = 'pass' | 'partial' | 'fail' | 'not_evaluated';
interface ExtractedVerdict {
  result: SupervisorVerdictResult;
  feedback: string;
  matchedCriteria: string[];
}

function extractSupervisorVerdict(output: unknown): ExtractedVerdict | null {
  if (!output || typeof output !== 'object') return null;
  const sup = (output as { _supervisor?: unknown })._supervisor;
  if (!sup || typeof sup !== 'object') return null;
  const s = sup as {
    result?: unknown;
    feedback?: unknown;
    matchedCriteria?: unknown;
  };
  const valid: SupervisorVerdictResult[] = ['pass', 'partial', 'fail', 'not_evaluated'];
  const result =
    typeof s.result === 'string' && (valid as string[]).includes(s.result)
      ? (s.result as SupervisorVerdictResult)
      : null;
  if (!result) return null;
  return {
    result,
    feedback: typeof s.feedback === 'string' ? s.feedback : '',
    matchedCriteria: Array.isArray(s.matchedCriteria)
      ? s.matchedCriteria.filter((m): m is string => typeof m === 'string')
      : [],
  };
}

const VERDICT_STYLES: Record<SupervisorVerdictResult, { label: string; color: string }> = {
  pass: {
    label: 'Pass',
    color: 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300 border-emerald-500/20',
  },
  partial: {
    label: 'Partial',
    color: 'bg-amber-500/10 text-amber-800 dark:text-amber-300 border-amber-500/30',
  },
  fail: {
    label: 'Fail',
    color: 'bg-red-500/10 text-red-700 dark:text-red-300 border-red-500/30',
  },
  not_evaluated: {
    label: 'Not evaluated',
    color: 'bg-zinc-500/10 text-zinc-700 dark:text-zinc-300 border-zinc-500/30',
  },
};

function StepRow({ step }: { step: HistoryExecutionStep }) {
  const [open, setOpen] = useState(false);
  const hasOutput = step.output !== null && step.output !== undefined;
  const hasError = !!step.error;
  const verdict = extractSupervisorVerdict(step.output);
  const expandable = hasOutput || hasError;

  let outputText: string | null = null;
  if (hasOutput) {
    try {
      outputText = JSON.stringify(step.output, null, 2);
    } catch {
      outputText = String(step.output);
    }
  }

  return (
    <div className="rounded-md border bg-card">
      <div
        className={cn(
          'flex items-center gap-3 px-3 py-2',
          expandable && 'cursor-pointer hover:bg-muted/40',
        )}
        onClick={() => expandable && setOpen((v) => !v)}
        role={expandable ? 'button' : undefined}
        aria-expanded={expandable ? open : undefined}
        title={expandable ? 'Toggle step detail' : undefined}
      >
        {expandable ? (
          open ? (
            <ChevronDown className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          ) : (
            <ChevronRight className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
          )
        ) : (
          <span className="w-3.5 flex-shrink-0" />
        )}
        <span className="text-[10px] font-mono text-muted-foreground w-6 text-right flex-shrink-0">
          #{step.displayOrder + 1}
        </span>
        <span className="text-xs font-medium min-w-[120px] truncate">
          {step.label || step.nodeKind || 'Step'}
        </span>
        {step.nodeKind && (
          <Badge
            variant="outline"
            className="text-[9px] font-mono bg-muted text-muted-foreground border-muted-foreground/20 flex-shrink-0"
          >
            {step.nodeKind}
          </Badge>
        )}
        <StatusBadge status={step.status} className="flex-shrink-0" />
        {/* #1101 — supervisor verdict badge surfaces the workflow's own
            quality contract at-a-glance, so operators don't have to expand
            the row and parse raw JSON to see a `fail` verdict. */}
        {verdict && (
          <Badge
            variant="outline"
            className={cn(
              'text-[10px] flex-shrink-0',
              VERDICT_STYLES[verdict.result].color,
            )}
            title="AI supervisor verdict against the workflow's success criteria"
          >
            Supervisor: {VERDICT_STYLES[verdict.result].label}
          </Badge>
        )}
        <span className="text-[10px] text-muted-foreground font-mono ml-auto flex-shrink-0">
          {formatDuration(step.durationMs)}
        </span>
      </div>
      {open && expandable && (
        <div className="px-6 pb-3 pt-1 space-y-2 text-xs border-t">
          {hasError && (
            <div className="flex items-start gap-2 rounded-md bg-red-500/5 border border-red-500/30 px-3 py-2">
              <AlertCircle
                className="h-3.5 w-3.5 text-red-700 dark:text-red-300 flex-shrink-0 mt-0.5"
                aria-hidden
              />
              <p className="text-xs text-red-700 dark:text-red-300 break-words">
                {step.error}
              </p>
            </div>
          )}
          {/* #1101 — Supervisor feedback panel. Renders the verdict reason
              + matched criteria so the operator can see WHY a step that
              ran cleanly was flagged as fail/partial by the AI supervisor.
              Without this, a Codex Test 5 scenario like "Agent returned
              HELLOHELLO when criterion required exactly HELLO" left the
              History detail showing nothing more than a Success badge —
              the contradiction lived in the API but never reached the UI. */}
          {verdict && (verdict.feedback || verdict.matchedCriteria.length > 0) && (
            <div
              className={cn(
                'rounded-md border px-3 py-2 space-y-1',
                verdict.result === 'fail' && 'bg-red-500/5 border-red-500/30',
                verdict.result === 'partial' && 'bg-amber-500/5 border-amber-500/30',
                verdict.result === 'pass' && 'bg-emerald-500/5 border-emerald-500/20',
                verdict.result === 'not_evaluated' && 'bg-zinc-500/5 border-zinc-500/30',
              )}
            >
              <p className="text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                Supervisor verdict
              </p>
              {verdict.feedback && (
                <p
                  className={cn(
                    'text-xs break-words',
                    verdict.result === 'fail' && 'text-red-700 dark:text-red-300',
                    verdict.result === 'partial' && 'text-amber-800 dark:text-amber-300',
                    verdict.result === 'pass' && 'text-emerald-700 dark:text-emerald-300',
                    verdict.result === 'not_evaluated' && 'text-zinc-700 dark:text-zinc-300',
                  )}
                >
                  {verdict.feedback}
                </p>
              )}
              {verdict.matchedCriteria.length > 0 && (
                <div className="flex flex-wrap gap-1 pt-1">
                  <span className="text-[10px] text-muted-foreground">
                    Matched criteria:
                  </span>
                  {verdict.matchedCriteria.map((c, i) => (
                    <Badge
                      key={`${i}-${c}`}
                      variant="outline"
                      className="text-[10px] font-mono"
                    >
                      {c}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
          )}
          {hasOutput && outputText && (
            <div>
              <p className="text-[10px] font-semibold text-muted-foreground uppercase tracking-wider mb-1">
                Output
              </p>
              <pre className="rounded-md bg-muted/40 px-3 py-2 text-[11px] font-mono whitespace-pre-wrap break-words max-h-[260px] overflow-auto">
                {outputText}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ── Page ────────────────────────────────────────────────────────────────

function HistoryExecutionDetailPage() {
  const { executionId } = Route.useParams();
  const navigate = useNavigate();
  const { data, isLoading, isError, error } = useHistoryExecution(executionId);

  return (
    <div className="p-6 space-y-6">
      {/* Back button */}
      <div>
        <Button
          variant="ghost"
          size="sm"
          className="gap-1.5"
          onClick={() => navigate({ to: '/automate/history', search: {} } as never)}
          title="Back to history"
        >
          <ArrowLeft className="h-3.5 w-3.5" aria-hidden />
          Back to History
        </Button>
      </div>

      {isLoading && (
        <div className="border border-dashed rounded-lg p-12 flex flex-col items-center justify-center text-center">
          <Loader2 className="h-8 w-8 text-muted-foreground animate-spin mb-3" />
          <p className="text-sm text-muted-foreground">Loading execution…</p>
        </div>
      )}

      {isError && (
        <div className="border border-red-500/30 bg-red-500/5 rounded-lg p-6">
          <div className="flex items-start gap-2">
            <AlertCircle className="h-4 w-4 text-red-700 dark:text-red-300 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-red-700 dark:text-red-300">
                Could not load execution
              </p>
              <p className="text-xs text-red-700 dark:text-red-300 mt-1">
                {error?.message ??
                  'The execution might have been deleted or you might not have access to view it.'}
              </p>
            </div>
          </div>
        </div>
      )}

      {data && <DetailBody detail={data} />}
    </div>
  );
}

function DetailBody({ detail }: { detail: HistoryExecutionDetail }) {
  return (
    <div className="space-y-6">
      {/* Header card */}
      <div className="rounded-lg border bg-card p-5 space-y-3">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="min-w-0">
            <div className="flex items-center gap-2 text-muted-foreground mb-1">
              <WorkflowIcon className="h-3.5 w-3.5" aria-hidden />
              <span className="text-[10px] uppercase tracking-wider font-medium">
                Workflow Execution
              </span>
            </div>
            <h1 className="text-lg font-semibold truncate">
              {detail.automationName ?? 'Unknown workflow'}
            </h1>
            <p className="text-[10px] text-muted-foreground font-mono mt-1">
              Execution ID: {detail.id}
            </p>
          </div>
          <StatusBadge status={detail.status} />
        </div>

        {detail.error && (
          <div className="flex items-start gap-2 rounded-md bg-red-500/5 border border-red-500/30 px-3 py-2">
            <AlertCircle
              className="h-3.5 w-3.5 text-red-700 dark:text-red-300 flex-shrink-0 mt-0.5"
              aria-hidden
            />
            <p className="text-xs text-red-700 dark:text-red-300 break-words">
              {detail.error}
            </p>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 pt-1">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
              Trigger
            </p>
            <p className="text-xs font-medium">{detail.triggeredBy}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
              Started
            </p>
            <p className="text-xs font-mono">
              {formatTimestamp(detail.startedAt)}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
              <span className="inline-flex items-center gap-1">
                <Timer className="h-2.5 w-2.5" aria-hidden /> Duration
              </span>
            </p>
            <p className="text-xs font-mono">
              {formatDuration(detail.durationMs)}
            </p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-muted-foreground mb-0.5">
              Steps
            </p>
            <p className="text-xs font-mono">
              <span className="text-emerald-700 dark:text-emerald-300 font-semibold">
                {detail.stepsCompleted}
              </span>
              <span className="text-muted-foreground"> / {detail.totalSteps}</span>
            </p>
          </div>
        </div>
      </div>

      {/* Step trace */}
      <div className="rounded-lg border bg-card p-5 space-y-3">
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-semibold">Step-by-Step Trace</h2>
          <span className="text-[10px] text-muted-foreground">
            {detail.steps.length} step{detail.steps.length !== 1 ? 's' : ''}
          </span>
        </div>
        {detail.steps.length === 0 ? (
          <p className="text-xs text-muted-foreground py-4 text-center">
            No step records were captured for this execution.
          </p>
        ) : (
          <div className="space-y-1.5">
            {detail.steps.map((step) => (
              <StepRow key={step.id} step={step} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
